import React, {Fragment} from "react";
import {get, set} from "../../../utils/dotutils";
import isEqual from 'lodash-es/isEqual';
import {bindActionCreators} from 'redux';
import {connectAdvanced} from "src/store";
import {setFieldValue, setFormValues, setPristineValues, setInitialValues, setEditorConfig} from "../../../actions";
import ComponentConfig from "./../ComponentConfig";
import {fileUpload, postData, sendRequest, trace} from "../../../utils/request";
import {getComponentConfig, loadComponent} from "../../../utils/components";
import ContentLoader from "../../layout/ContentLoader";
import classNames from "classnames";
import Modal from "../../layout/Modal";
import {showModal} from '../../../actions';
import AskBeforeUnload from "./AskBeforeUnload";
import {addEditor, createEditorReducer, REDUX_EDITOR_INIT} from "../../../reducers/editors";
import {store} from "src/store";
import parse_str from "locutus/php/strings/parse_str";
import Box from "../../layout/Box";
import Portal from "../../layout/Portal";
import {Helmet} from "react-helmet";
import axios from "axios";
import Button from "../Button";
import PropsProxy from "../../layout/PropsProxy";

class EditorErrorBoundary extends React.Component {

    state = {
        error: null,
        errorInfo: null,
        collapsed: false
    }

    componentDidCatch(error, errorInfo) {
        // Catch errors in any child components and re-renders with an error message
        this.setState({
            error: error,
            errorInfo: errorInfo
        });
    }

    toggleCollapse = () => {
        this.setState({
            collapsed: !this.state.collapsed
        })
    }

    render() {
        if (this.state.error) {
            // Fallback UI if an error occurs
            return (
                <div className="box box-danger box-solid">
                    <div className="box-header with-border">
                        {this.state.error && this.state.error.toString()}
                        <div className="box-tools pull-right">
                            <Button onClick={this.toggleCollapse} type="button" className="btn btn-box-tool" data-widget="collapse" icon={{lib: "fa", name: classNames({"minus": !this.state.collapsed, "plus": this.state.collapsed})}} />
                        </div>
                    </div>
                    {!this.state.collapsed && <div className="box-body">
                        <div>Component Stack Error Details:</div>
                        <pre>{this.state.error.stack}</pre>
                        <pre>{this.state.errorInfo.componentStack}</pre>
                    </div>}
                </div>
            );
        }
        // component normally just renders children
        return this.props.children;
    }
}

const bindEditorActions = (editorName, bindActions) => {
    const actions = {};
    Object.keys(bindActions).map((actionName) => {
        actions[actionName] = function () {
            const args = [...Array.prototype.slice.call(arguments)];
            args.unshift(editorName);
            return bindActions[actionName].apply(null, args);
        }
    });
    return actions;
}

/*const dirtyForms = {};
window.onbeforeunload = () => {
    return Object.keys(dirtyForms).length > 0 ? Object.keys(dirtyForms).map((key) => dirtyForms[key]).join("\n") : undefined;
};*/

export const referenceActionData = async (data, componentConfig) => {
    let d = {...data};
    await Promise.all(Object.keys(componentConfig.fielddefs).map(async (fieldname) => {
        if (data.hasOwnProperty(fieldname)) {
            const type = (componentConfig.fielddefs[fieldname].type || "attribute");
            if (type === "object" && (data[fieldname] instanceof Object && data[fieldname] !== null)) {
                const config = await getComponentConfig(componentConfig.fielddefs[fieldname].object);
                d[fieldname] = data[fieldname][config.idfield];
            } else {
                d[fieldname] = data[fieldname];
            }
        }
    }));
    return d;
}

export class ComponentEditor extends React.Component {

    static placeHolder = PlaceHolderComponent;

    state = {
        isLoading: false,
        askBeforeUnload: false,
        askBeforeDelete: false,
        saveSuccess: null,
        validationError: null,
        deleteSuccess: null,
    }

    constructor(props) {
        super(props);
        if (this.props.componentConfig) {
            this.props.setEditorConfig(this.props.componentConfig);
        }
    }

    componentDidMount () {
        if(this.props[this.props.componentConfig.idfield]) {
            if (!isEqual(this.props[this.props.componentConfig.idfield] + "", this.props.values[this.props.componentConfig.idfield] + "")) {
                this.setState({isLoading: true}, async () => {
                    await this.loadForm({[this.props.componentConfig.idfield]: this.props[this.props.componentConfig.idfield]});
                    this.setState({isLoading: false});
                });
            }
        } else if (this.props.filter) {
            this.loadByFilter && this.loadByFilter.abort();
            this.setState({isLoading: true}, async () => {
                this.loadByFilter = loadComponent(this.props.componentConfig.componentname, {filter: this.props.filter}, [this.props.componentConfig.idfield]);
                this.loadByFilter.then(async (data) => {
                    if (data[this.props.componentConfig.idfield]) {
                        await this.loadForm({[this.props.componentConfig.idfield]: data[this.props.componentConfig.idfield]});
                    } else if (this.props.values[this.props.componentConfig.idfield]) {
                        await this.pristineForm();
                    }
                    this.setState({isLoading: false});
                });
            });
        }
    }

    componentWillUnmount() {
        this.loadByFilter && this.loadByFilter.abort();
    }

    componentDidUpdate(prevProps, prevState, prevContext) {
        if (prevProps[prevProps.componentConfig.idfield] + "" !== "" + this.props[this.props.componentConfig.idfield] && this.props.values[this.props.componentConfig.idfield] + "" === "" + this.props[this.props.componentConfig.idfield]) {
            if (this.state.askBeforeUnload) {
                this.promisedSetState({askBeforeUnload: false, isLoading: false});
            }
        }
        if(!isEqual(this.props.filter, prevProps.filter) || !isEqual(prevProps.componentConfig, this.props.componentConfig) || prevProps[prevProps.componentConfig.idfield] + "" !== "" + this.props[this.props.componentConfig.idfield]) {
            this.props.setEditorConfig(this.props.componentConfig);
            if(this.props[this.props.componentConfig.idfield]) {
                if (!isEqual(this.props[this.props.componentConfig.idfield] + "", this.props.values[this.props.componentConfig.idfield] + "")) {
                    this.loadByFilter && this.loadByFilter.abort();
                    this.setState({isLoading: true}, async () => {
                        await this.loadForm({[this.props.componentConfig.idfield]: this.props[this.props.componentConfig.idfield]});
                        await this.promisedSetState({isLoading: false});
                    });
                }
            } else if (JSON.stringify(this.props.filter) !== JSON.stringify(prevProps.filter)) {
                if (
                    (Array.isArray(this.props.filter) && this.props.filter.length > 0)
                    ||
                    this.props.filter instanceof Object
                ) {
                    this.loadByFilter = loadComponent(this.props.componentConfig.componentname, {filter: this.props.filter}, [this.props.componentConfig.idfield]);
                    this.setState({isLoading: true}, async () => {
                        this.loadByFilter.then(async (data) => {
                            if (data[this.props.componentConfig.idfield]) {
                                await this.loadForm({[this.props.componentConfig.idfield]: data[this.props.componentConfig.idfield]});
                            } else {
                                await this.pristineForm();
                            }
                            await this.promisedSetState({isLoading: false});
                        });
                        this.loadByFilter.catch(async () => {
                            await this.pristineForm();
                            await this.promisedSetState({isLoading: false});
                        });
                    });
                } else {
                    this.loadByFilter && this.loadByFilter.abort();
                }
            } else {
                this.loadByFilter && this.loadByFilter.abort();
            }
        }
        if (!isEqual(this.props.formId, prevProps.formId)) {
            try {
                if (this.props.onFormLoaded) {
                    this.props.onFormLoaded(prevProps.values, this.props.values, this.props.componentConfig);
                }
            } catch (e) {
                console.error(e);
            }
        }
    }

    promisedSetState = (newState) => {
        return new Promise((resolve) => {
            this.setState(newState, () => {
                resolve()
            });
        });
    }

    beforeUnload = (forceAsk) => {
        if (forceAsk || this.props.beforeUnload === undefined || this.props.beforeUnload === true) {
            return new Promise((resolve, reject) => {
                if (this.props.dirty || forceAsk) {
                    this.promisedSetState({askBeforeUnload: {resolve,reject}});
                } else {
                    resolve();
                }
            });
        } else {
            return Promise.resolve();
        }
    }

    beforeDelete = () => {
        return new Promise((resolve, reject) => {
            this.promisedSetState({
                askBeforeDelete: [
                    {
                        text: 'Törlés',
                        props: {
                            color: "danger",
                            timeout: 3,
                            focus: true
                        },
                        onClick: async () => {
                            try {
                                await this.promisedSetState({askBeforeDelete: false});
                                resolve();
                            } catch (e) {
                                console.error(e);
                                await this.promisedSetState({askBeforeDelete: false, deleteSuccess: e.message});
                                reject(e);
                            }
                        }
                    },
                    {
                        text: 'Mégse',
                        onClick: async () => {
                            await this.promisedSetState({askBeforeDelete: false});
                            reject();
                        }
                    }
                ]
            });
        });
    }

    importFile = async (field, value) => {
        return await this.processAction(async () => {
            if (this.props.importFile) {
                return await this.props.importFile(field, value);
            }
        })
    }

    getFieldProps = (name) => {
        if (this.props.getFieldProps) {
            const fieldProps = this.props.getFieldProps(name);
            if (this.props.presets) {
                const notExistsValue = "notExistsValue" + (new Date().getTime()) + "";
                const value = get(this.props.presets, name, notExistsValue);
                if (value !== notExistsValue) {
                    fieldProps.disabled = true;
                }
            }
            if (this.props.componentConfig) {
                if (this.props.componentConfig.fielddefs) {
                    if (this.props.componentConfig.fielddefs[name] && this.props.componentConfig.fielddefs[name].required) {
                        fieldProps.required = true;
                    }
                }
            }
            if (this.props.componentAction) {
                const action = (this.props.componentConfig.actions || {})[this.props.componentAction];
                if (action) {
                    if (action.fields) {
                        if (action.fields[name] && action.fields[name].required) {
                            fieldProps.required = true;
                        }
                    }
                }
            }
            if (this.state.isLoading) {
                fieldProps.disabled = true;
            }
            return fieldProps;
        } else {
            console.warn(`No getFieldProps function set for componentEditor!`);
        }
    }

    getFieldsProps = (names) => {
        if (this.props.getFieldsProps) {
            const props = this.props.getFieldsProps(names);
            names.map((name) => props[name] = this.getFieldProps(name));
            return props;
        } else {
            console.warn(`No getFieldsProps function set for componentEditor!`);
        }
    }

    setFormValues = async (data, askBeforeUnload = false) => {
        if (this.props.setFormValues) {
            if (!!askBeforeUnload) {
                await this.beforeUnload();
            }
            return await this.props.setFormValues(data);
        } else {
            console.warn(`No setFormValues function set for componentEditor!`);
        }
    }

    setPristineValues = async (data) => {
        if (this.props.setPristineValues) {
            return await this.props.setPristineValues(data);
        } else {
            console.warn(`No setPristineValues function set for componentEditor!`);
        }
    }

    saveForm = async (event, modalOnlyOnError = false) => {
        let data = {};
        try {
            await this.promisedSetState({isLoading: true});
            if (this.props.saveForm) {
                data = await this.props.saveForm();
                if (!modalOnlyOnError) {
                    await this.promisedSetState({saveSuccess: true});
                }
            } else {
                console.warn(`No saveForm function set for componentEditor!`);
            }
            await this.promisedSetState({isLoading: false});
            return data;
        } catch (e) {
            const throwable = e;
            console.error(e);

            let error;
            let header = "Sikertelen művelet";
            if (!(e instanceof axios.Cancel)) {
                const validation = (data) => {
                    header = "Hibás adat" + (Object.keys(data?.messages || {}).length > 1 ? "ok" : "");
                    error = <div>
                        {data?.message || data?.msg}
                        {Object.keys(data?.messages || {}).map((key) => {
                            if (Array.isArray(data.messages[key])) {
                                return data.messages[key].map((message, idx) => {
                                    return <div key={idx}>{message}</div>;
                                });
                            }
                            return <div>{data.messages[key]}</div>;
                        })}
                    </div>
                }
                if (e?.validation) {
                    validation(e);
                } else if (e?.response?.data?.validation) {
                    validation(e.response.data);
                } else if (e?.response?.data?.error) {
                    error = <Fragment>{e.response.data.message || e.response.data.msg ? <div><b>{e.response.data.message || e.response.data.msg}</b></div> : null}{trace(e)}</Fragment>
                } else if (e?.response?.data?.msg) {
                    error = e.response.data.msg;
                } else if (e?.response?.data?.messages) {
                    error = <Fragment>{Object.entries((entry) => {
                        return <div key={entry[0]}>{entry[1]}</div>
                    })}</Fragment>;
                } else if (e?.response?.data?.message) {
                    error = e.response.data.message;
                } else if (e instanceof Error) {
                    error = e.message;
                }
                error = <Portal>
                    <Modal
                        state="on"
                        theme="warning"
                        size="medium"
                        header={header}
                        buttons={[
                            {
                                text: 'Ok',
                                onClose: true,
                                onClick: async () => {
                                    await this.promisedSetState({validationError: null});
                                }
                            }
                        ]}
                    >
                        {error}
                    </Modal>
                </Portal>
            } else {
                error = <Portal>
                    <Modal
                        state="on"
                        theme="warning"
                        size="medium"
                        header="Sikertelen művelet"
                        buttons={[
                            {
                                text: 'Ok',
                                onClose: true,
                                onClick: async () => {
                                    await this.promisedSetState({validationError: null});
                                }
                            }
                        ]}
                    >
                        {e}
                    </Modal>
                </Portal>;
            }

            await this.promisedSetState({
                isLoading: false,
                askBeforeUnload: false,
                saveSuccess: null,
                validationError: error
            });
            throw throwable;
        }
    }

    initialForm = async () => {
        await this.promisedSetState({isLoading: true});
        if (this.props.initialForm) {
            try {
                await this.beforeUnload();
                const initialValues = await this.props.initialForm();
                if(this.props.setContent) {
                    await this.props.setContent(this.props.componentEditor, initialValues[this.props.componentConfig.idfield] ? {[this.props.componentConfig.idfield]: initialValues[this.props.componentConfig.idfield]} : {});
                }
            } catch (e) {
                console.info("initialForm canceled, stay on dirty form!", e);
                await this.promisedSetState({isLoading: false});
                throw e;
            }
        } else {
            console.warn(`No initialForm function set for componentEditor!`);
        }
        await this.promisedSetState({isLoading: false});
    }

    pristineForm = async (askBeforeUnload = true) => {
        await this.promisedSetState({isLoading: true});
        if (this.props.pristineForm) {
            try {
                if (!!askBeforeUnload) {
                    await this.beforeUnload();
                }
                let pristineValues = await this.props.pristineForm();
                if (this.props.setContent) {
                    await this.props.setContent(this.props.componentEditor, pristineValues[this.props.componentConfig.idfield] ? {[this.props.componentConfig.idfield]: pristineValues[this.props.componentConfig.idfield]} : {});
                }
            } catch (e) {
                console.info("pristineForm canceled, stay on dirty form!", e);
                await this.promisedSetState({isLoading: false});
                throw e;
            }
        } else {
            console.warn(`No pristineForm function set for componentEditor!`);
        }
        await this.promisedSetState({isLoading: false});
    }

    deleteForm = async (e, data = null) => {
        try {
            await this.promisedSetState({isLoading: true});
            if (this.props.deleteForm) {
                await this.beforeDelete();
                await this.props.deleteForm(e, data);
                await this.promisedSetState({deleteSuccess: true});
                if (!data) {
                    await this.pristineForm();
                }
            } else {
                console.warn(`No deleteForm function set for componentEditor!`);
            }
            await this.promisedSetState({isLoading: false});
        } catch (e) {
            if (e === undefined) {
                await this.promisedSetState({isLoading: false, askBeforeDelete: false, deleteSuccess: null});
            } else {
                console.error(e);
                e = e?.response?.data || e;
                await this.promisedSetState({
                    isLoading: false,
                    askBeforeDelete: false,
                    deleteSuccess: <Fragment><div>{e.msg || e.message}</div>{(e && ((e.error && trace(e)) || e.message))}</Fragment>
                });
            }
        }
    }

    loadForm = async (data) => {
        await this.promisedSetState({isLoading: true});
        if (this.props.loadForm) {
            let formData = {};
            try {
                const newId = (data && data[this.props.componentConfig.idfield]) || null;
                const oldId = (this.props.values && this.props.values[this.props.componentConfig.idfield]) || null;
                if (!isEqual(oldId, newId)) {
                    if (data) {
                        try {
                            await this.beforeUnload();
                        } catch (e) {
                            if(this.props.setContent) {
                                await this.props.setContent(this.props.componentEditor, this.props.values[this.props.componentConfig.idfield]? {[this.props.componentConfig.idfield]: this.props.values[this.props.componentConfig.idfield]} : {});
                            }
                            throw e;
                        }
                        formData = await this.props.loadForm(data);
                        if (this.props.setContent) {
                            await this.props.setContent(this.props.componentEditor, formData[this.props.componentConfig.idfield] ? {[this.props.componentConfig.idfield]: formData[this.props.componentConfig.idfield]} : {});
                        }
                    } else {
                        await this.pristineForm();
                    }
                }
                /*if (formData[this.props.componentConfig.idfield] && this.props.history) {
                    this.props.history.push(this.props.location.pathname + "?" + http_build_query({option: this.props.componentName, [this.props.componentConfig.idfield]: formData[this.props.componentConfig.idfield]}));
                }*/
            } catch (e) {
                console.info("loadForm canceled, stay on dirty form!", e);
                await this.promisedSetState({isLoading: false});
                throw e;
            }
            await this.promisedSetState({isLoading: false});
            return formData;
        } else {
            console.warn(`No loadForm function set for componentEditor!`);
        }
        await this.promisedSetState({isLoading: false});
    }

    reloadForm = async ({silent = false} = {}) => {
        if (!silent) {
            await this.promisedSetState({isLoading: true});
        }
        if (this.props.reloadForm) {
            let formData = {};
            try {
                formData = await this.props.reloadForm();
                if (this.props.setContent) {
                    await this.props.setContent(this.props.componentEditor, formData[this.props.componentConfig.idfield] ? {[this.props.componentConfig.idfield]: formData[this.props.componentConfig.idfield]} : {});
                }
                /*if (formData[this.props.componentConfig.idfield] && this.props.history) {
                    this.props.history.push(this.props.location.pathname + "?" + http_build_query({option: this.props.componentName, [this.props.componentConfig.idfield]: formData[this.props.componentConfig.idfield]}));
                }*/
            } catch (e) {
                console.info("reloadForm canceled, stay on dirty form!", e);
                await this.promisedSetState({isLoading: false});
                throw e;
            }
            await this.promisedSetState({isLoading: false});
            return formData;
        } else {
            console.warn(`No reloadForm function set for componentEditor!`);
        }
        if (!silent) {
            await this.promisedSetState({isLoading: false});
        }
    }

    setFieldValue = async (field, value) => {
        if (this.props.setFieldValue) {
            return await this.props.setFieldValue(field, value);
        } else {
            console.warn(`No setFieldValue function set for componentEditor!`);
        }
    }

    processAction = async (action) => {
        await this.promisedSetState({isLoading: true});
        let response = undefined;
        try {
            if (this.props.processAction) {
                response = await this.props.processAction(action, {values: this.props.values, componentConfig: this.props.componentConfig});
                try {
                    if (this.props.onProcessAction) {
                        this.props.onProcessAction(response, this.props.values, this.props.componentConfig);
                    }
                } catch (e) {
                    console.error(e);
                }
                if (response !== false) {
                    await this.reloadForm();
                    if (response.success !== undefined) {
                        if (response.success) {
                            if (response.newWindow) {
                                window.open(response.newWindow, '_blank');
                            }
                            if (response.redirect) {
                                let params = {};
                                parse_str(response.redirect.substr(1), params);
                                if (this.props.setContent && params.option) {
                                    this.props.setContent(params.option, params);
                                } else {
                                    console.warn(`No redirect option parameter!`);
                                }
                            }
                        } else if (!response.success) {
                            if (response.msg) {
                                store.dispatch(showModal({
                                    theme: "danger",
                                    header: "",
                                    text: response.msg
                                }));
                            }
                        }
                    }
                }
            } else {
                console.warn(`No processAction function set for componentEditor!`);
            }
        } catch (e) {
            console.error(e);
            await this.promisedSetState({isLoading: false});
            throw e;
        }
        await this.promisedSetState({isLoading: false});
        return response;
    }

    handleSubmit = (e) => {
        e.preventDefault();
    }

    hasPermission = (permission, items = null) => {
        items = items || (this.props.menu && this.props.menu.items);
        return !!items.find((item) => {
            if (item.token === permission) {
                return true;
            } else if (item.items) {
                return this.hasPermission(permission, item.items);
            }
        });
    }

    render() {
        // Ezt ha teljesen kész vagyunk tegyük vissza!!
        /*if (!(this.hasPermission(this.props.componentEditor) || (this.props.user && this.props.user.hasPermission(this.props.componentEditor)))) {
            return <Box header="Hiba" theme="danger">
                Nincs jogosultságod!
            </Box>;
        }*/
        const thisProps = {...this.props};
        delete thisProps.children;
        let children = null;
        children = <PropsProxy
            {...{
                ...thisProps,
                isLoading: this.state.isLoading,
                beforeUnload: this.beforeUnload,
                processAction: this.processAction,
                importFile: this.importFile,
                getFieldProps: this.getFieldProps,
                getFieldsProps: this.getFieldsProps,
                setFormValues: this.setFormValues,
                setPristineValues: this.setPristineValues,
                saveForm: this.saveForm,
                loadForm: this.loadForm,
                reloadForm: this.reloadForm,
                initialForm: this.initialForm,
                pristineForm: this.pristineForm,
                deleteForm: this.deleteForm,
                handleSubmit: this.handleSubmit,
                filter: thisProps.listFilter !== undefined ? thisProps.listFilter : undefined
            }}
        >
            {this.props.children}
        </PropsProxy>
        /*if (this.props.dirty) {
            dirtyForms[this.props.componentName] = "There are unsaved data on: " + this.props.componentName;
        } else {
            delete dirtyForms[this.props.componentName];
        }*/
        let askBeforeUnload = null;
        if (this.state.askBeforeUnload) {
            let askBeforeUnloadProps = {
                ...this.props,
                isLoading: this.props.isLoading || this.state.isLoading,
                resolve: async (reason) => {
                    if (reason === true) {
                        await this.saveForm();
                    }
                    this.state.askBeforeUnload.resolve(reason);
                    await this.promisedSetState({askBeforeUnload: false});
                },
                reject: async (reason) => {
                    console.error(reason);
                    this.state.askBeforeUnload.reject(reason);
                    await this.promisedSetState({askBeforeUnload: false, saveSuccess: reason?.message || null});
                }
            }
            if (!this.props.askBeforeUnload) {
                askBeforeUnload = <AskBeforeUnload
                    {...askBeforeUnloadProps}
                />;
            } else {
                if (this.props.askBeforeUnload.type) {
                    askBeforeUnload = <this.props.askBeforeUnload.type
                        {...this.props.askBeforeUnload.props}
                        {...askBeforeUnloadProps}
                    />;
                } else {
                    askBeforeUnload = <this.props.askBeforeUnload
                        {...askBeforeUnloadProps}
                    />
                }
            }
        }
        return (
            <Fragment>
                {this.props.section && this.props.section.heading && <Helmet>
                    <title>{window.envBooqadmin.title} - {this.props.section.heading} {this.props.values[this.props.componentConfig.idfield] ? `#${this.props.values[this.props.componentConfig.idfield]}` : ''}</title>
                </Helmet>}
                {children}
                {askBeforeUnload}
                {this.state.validationError}
                {this.state.saveSuccess !== null && <Portal>
                    <Modal
                        state={this.state.saveSuccess !== null ? "on" : "off"}
                        theme={this.state.saveSuccess === true ? "success" : "warning"}
                        size={this.state.saveSuccess !== null && this.state.saveSuccess !== true ? "large" : "small"}
                        header="Mentés"
                        timeout={(this.state.saveSuccess === true && 2000) || false}
                        buttons={[
                            {
                                text: 'Ok',
                                onClose: true,
                                focus: true,
                                props: {timeout: (this.state.saveSuccess === true && 2) || false, disableBeforeTimeout: false, color: this.state.saveSuccess === true ? "success" : "warning"},
                                onClick: async () => {
                                    await this.promisedSetState({saveSuccess: null});
                                }
                            }
                        ]}
                    >
                        {this.state.saveSuccess !== null && this.state.saveSuccess !== true ? (this.state.saveSuccess || "Mentés sikertelen") : "Mentés sikeres!"}
                    </Modal>
                </Portal>}
                {this.state.askBeforeDelete && <Portal>
                    <Modal
                        state={this.state.askBeforeDelete ? "on" : "off"}
                        theme={"default"}
                        header={"Törlés"}
                        buttons={this.state.askBeforeDelete || []}
                    >
                        Biztosan törlöd ezt a tételt?
                    </Modal>
                </Portal>}
                {this.state.deleteSuccess && <Portal>
                    <Modal
                        state={this.state.deleteSuccess !== null ? "on" : "off"}
                        theme={this.state.deleteSuccess === true ? "success" : "warning"}
                        size={this.state.deleteSuccess !== null && this.state.deleteSuccess !== true ? "large" : "small"}
                        header={"Törlés"}
                        timeout={(this.state.deleteSuccess === true && 1500) || false}
                        buttons={[
                            {
                                text: 'Ok',
                                onClose: true,
                                focus: true,
                                onClick: async () => {
                                    await this.promisedSetState({deleteSuccess: null});
                                }
                            }
                        ]}
                    >
                        {this.state.deleteSuccess !== null && this.state.deleteSuccess !== true ? (this.state.deleteSuccess || "Törlés sikertelen!") : "Törlés sikeres!"}
                    </Modal>
                </Portal>}
            </Fragment>
        );
    }

}

export const createReduxEditor = ({componentName, WrappedComponent, reduxEditor = null, props = {}, dirtyFlag = null}) => {

    class ReduxComponentEditor extends React.Component {

        constructor(props) {
            super(props);
        }

        processAction = async (action, {values, componentConfig}) => {
            let result = null;
            if (action) {
                const componentAction = async (actionName, reparams = {}) => {
                    return await postData({
                        action: componentConfig.componentname + '.' + actionName,
                        [componentConfig.idfield]: values[componentConfig.idfield],
                        ...reparams
                    });
                }
                const componentDataAction = async (actionName, reqparams = {}) => {
                    return await postData({
                        action: componentConfig.componentname + '.' + actionName,
                        data: values,
                        ...reqparams
                    });
                }
                result = await action({componentAction, componentDataAction});
            }
            if (result === null || result === undefined || result || result.success) {
                return result;
            } else {
                throw result;
            }
        }

        loadForm = async (data) => {
            const componentConfig = await getComponentConfig(componentName);
            let formData = {};
            if (data && data[componentConfig.idfield]) {
                let reqParams = {
                    option: componentName,
                    view: props.view || "default",
                    showdeleted: props.showdeleted,
                    resptype: "json",
                    [componentConfig.idfield]: data[componentConfig.idfield]
                };
                if (props.showdeleted) {
                    reqParams.showdeleted = props.showdeleted;
                }
                formData = await sendRequest(reqParams);
                await this.props.setFormValues(formData, true);
            }
            try {
                if (this.props.loadForm) {
                    await this.props.loadForm(formData, componentConfig, this.props);
                }
            } catch (e) {
                console.error(e);
            }
            return formData;
        }

        reloadForm = async () => {
            const componentConfig = await getComponentConfig(componentName);
            const data = {...this.props.values, ...(this.props.presets || {})};
            let formData = {};
            if (data && data[componentConfig.idfield]) {
                formData = await sendRequest({
                    option: componentName,
                    view: props.view || "default",
                    resptype: "json",
                    [componentConfig.idfield]: data[componentConfig.idfield]
                });
                await this.props.setFormValues(formData, true);
            }
            try {
                if (this.props.reloadForm) {
                    await this.props.reloadForm(formData, componentConfig, this.props);
                }
            } catch (e) {
                console.error(e);
            }
            return formData;
        }

        saveForm = async () => {
            const componentConfig = await getComponentConfig(componentName);
            let data = {...this.props.values, ...(this.props.presets || {})};
            let params = {};
            let action = (!data[componentConfig.idfield] ? "create" : "update");
            if (props.componentActions) {
                const actionsData = props.componentActions instanceof Function ? (await props.componentActions(action, data, params, componentConfig)) : (typeof props.componentActions === "object" ? props.componentActions[action] : props.componentActions);
                if (typeof(actionsData) === "string") {
                    action = actionsData;
                } else if (typeof(actionsData) === "object") {
                    action = actionsData.action || action;
                    data = actionsData.data || data;
                    params = actionsData.params || params;
                }
            }

            /*if (Validators.editors[this.props.componentEditor]) {
                const notValid = Validator(Validators.editors[this.props.componentEditor], data, '', this.props.componentEditor);
                if (notValid) {
                    console.log(notValid);
                    throw notValid;
                }
            }*/

            let requestData = {
                action: componentName + "." + action,
                view: props.view || "default",
                ...params,
                data
            };
            if (data[componentConfig.idfield]) {
                requestData[componentConfig.idfield] = data[componentConfig.idfield];
            }
            const response = await postData(requestData, {}, {showError: false});
            if (response.success) {
                if (response.data[componentConfig.idfield]) {
                    await this.props.setInitialValues(response.data);
                    await this.props.setFormValues(response.data);
                }
                try {
                    if(this.props.saveForm) {
                        await this.props.saveForm(response.data, componentConfig, {...this.props, ...this});
                    }
                } catch (e) {
                    console.error(e);
                }
                return response.data;
            } else {
                console.error(response);
                throw response;
            }
        }

        deleteForm = async (e, data = null) => {
            const componentConfig = await getComponentConfig(componentName);
            data = data || {...this.props.values, ...(this.props.presets || {})};
            let params = {};
            let action = "delete";
            if (props.componentActions) {
                const actionsData = props.componentActions instanceof Function ? (await props.componentActions(action, data, params, componentConfig)) : (typeof props.componentActions === "object" ? props.componentActions["delete"] : props.componentActions);
                if (typeof(actionsData) === "string") {
                    action = actionsData;
                } else if (typeof(actionsData) === "object") {
                    action = actionsData.action || action;
                    data = actionsData.data || data;
                    params = actionsData.params || params;
                }
            }
            const id = data[componentConfig.idfield];
            if (id) {
                const response = await postData({
                    action: componentName + "." + action,
                    [componentConfig.idfield]: id,
                    ...params,
                }, {}, {showError: false});
                if (response.success) {
                    //await this.props.setInitialValues(this.props.pristineValues);
                    //await this.props.setFormValues(this.props.pristineValues);
                    try {
                        if(this.props.deleteForm) {
                            await this.props.deleteForm(componentConfig, this.props);
                        }
                    } catch (e) {
                        console.error(e);
                    }
                } else {
                    console.error(response);
                    throw response;
                }
            }
        }

        importFile = async (field, value) => {
            const componentConfig = await getComponentConfig(componentName);
            let data = {...this.props.values, ...(this.props.presets || {})};
            let params = {};
            let action = "import";
            if (props.componentActions) {
                const actionsData = props.componentActions instanceof Function ? (await props.componentActions(action, data, params, componentConfig)) : (typeof props.componentActions === "object" ? props.componentActions["delete"] : props.componentActions);
                if (typeof(actionsData) === "string") {
                } else if (typeof(actionsData) === "object") {
                    data = actionsData.data || data;
                    params = actionsData.params || params;
                }
            }
            return await fileUpload({action: componentConfig.componentname + ".import", file: value, data, ...params});
        }

        getFieldProps = (name) => {
            let props = {};
            if (this.props.fieldProps) {
                if (this.props.fieldProps instanceof Function) {
                    props = this.props.fieldProps(name);
                } else {
                    props = this.props.fieldProps[name] || {};
                }
            }
            props.formId = this.props.formId;
            return props;
        }

        getFieldsProps = (names) => {
            const props = {};
            names.map((name) => props[name] = this.getFieldProps(name));
            return props;
        }

        initialForm = async () => {
            await this.props.setFormValues(this.props.initialValues, true);
            try {
                if (this.props.initialForm) {
                    await this.props.initialForm(this.props.initialValues);
                }
            } catch (e) {
                console.log(e);
            }
            return this.props.initialValues;
        }

        pristineForm = async () => {
            let pristineValues = JSON.parse(JSON.stringify(this.props.pristineValues));
            try {
                if (this.props.pristineForm) {
                    pristineValues = await this.props.pristineForm(JSON.parse(JSON.stringify(pristineValues))) || pristineValues;
                }
            } catch (e) {
                console.log(e);
            }
            await this.props.setFormValues(pristineValues, true);
            return pristineValues;
        }

        render() {
            if (this.props.editorIsLoading) {
                return this.props.loader !== undefined ? this.props.loader : <ContentLoader/>;
            }
            const values = {...this.props.values, ...(this.props.presets || {})};
            const initialValues = {...this.props.initialValues, ...(this.props.presets || {})};

            const props = {...this.props, values, initialValues, ...{
                    fieldProps: this.props.fieldProps,
                    setFieldValue: this.props.setFieldValue,
                    setFormValues: this.props.setFormValues,
                    setPristineValues: this.props.setPristineValues,
                    setInitialValues: this.props.setInitialValues,
                    processAction: this.processAction,
                    importFile: this.importFile,
                    loadForm: this.loadForm,
                    reloadForm: this.reloadForm,
                    saveForm: this.saveForm,
                    initialForm: this.initialForm,
                    pristineForm: this.pristineForm,
                    getFieldProps: this.getFieldProps,
                    getFieldsProps: this.getFieldsProps,
                    deleteForm: this.deleteForm
                }};
            return (<EditorErrorBoundary>
                <ComponentConfig {...props} componentName={componentName} loader={() => {
                    if (WrappedComponent.placeHolder) {
                        const PlaceHolderComp = WrappedComponent.placeHolder;
                        return <PlaceHolderComp />;
                    } else {
                        return <ContentLoader />;
                    }
                }}>
                    <ComponentEditor>
                        <WrappedComponent />
                    </ComponentEditor>
                </ComponentConfig>
            </EditorErrorBoundary>);
        }
    }

    return connectAdvanced((dispatch) => {
            const bindActions = bindActionCreators({
                setFieldValue,
                setFormValues,
                setPristineValues,
                setInitialValues,
                setEditorConfig,
            }, dispatch);
            return (store, nextOwnProps) => {
                const componentEditor = nextOwnProps.editorContext || reduxEditor || componentName;
                if (store && store.editors[componentEditor] === undefined) {
                    try {
                        addEditor(componentEditor, createEditorReducer(componentEditor, props.initialState || {}));
                        dispatch({type: REDUX_EDITOR_INIT});
                    } catch (e) {

                    }
                    return {editorIsLoading: true};
                }
                const formId = store.editors[componentEditor].formId;
                const values = store.editors[componentEditor].values;
                const pristineValues = store.editors[componentEditor].pristineValues;
                const initialValues = store.editors[componentEditor].initialValues;
                let dirty;
                if (dirtyFlag) {
                    dirty = !!(dirtyFlag instanceof Function ? dirtyFlag(values, initialValues, pristineValues) : dirtyFlag);
                } else {
                    dirty = store.editors[componentEditor].dirty;
                }
                const _setEditorConfig = (config) => {
                    dispatch(setEditorConfig(componentEditor, config));
                }
                return {
                    user: store.app.user,
                    menu: store.app.template.menu,
                    ...bindEditorActions(componentEditor, bindActions),
                    setEditorConfig: _setEditorConfig,
                    initialValues,
                    pristineValues,
                    values,
                    dirty,
                    componentName,
                    componentEditor,
                    selection: values,
                    formId,
                    ...props,
                    ...nextOwnProps
                };
            }
        }
    )(ReduxComponentEditor);
};

export const createBackendEditor = ({componentName, WrappedComponent, editor = null, props = {}, dirtyFlag = null}) => {

    class BackendComponentEditor extends React.Component {

        processAction = async (action, {values, componentConfig}) => {
            let result = null;
            if (action) {
                const componentAction = async (actionName, reparams = {}) => {
                    return await postData({
                        action: componentConfig.componentname + '.' + actionName,
                        [componentConfig.idfield]: values[componentConfig.idfield],
                        ...reparams
                    });
                }
                const componentDataAction = async (actionName, reqparams = {}) => {
                    return await postData({
                        action: componentConfig.componentname + '.' + actionName,
                        data: values,
                        ...reqparams
                    });
                }
                result = await action({componentAction, componentDataAction});
            }
            if (result === null || result === undefined || result || result.success) {
                return result;
            } else {
                throw result;
            }
        }

        constructor(props) {
            super(props);
            if (this.props.componentConfig) {
                this.props.setEditorConfig(this.props.componentConfig);
            }
        }

        loadForm = async (data) => {
            const componentConfig = this.props.componentConfig;
            let formData = {};
            if (data && data[componentConfig.idfield]) {
                formData = await sendRequest({
                    option: componentName,
                    view: props.view || "default",
                    resptype: "json",
                    [componentConfig.idfield]: data[componentConfig.idfield]
                });
                await this.props.setFormValues(formData, true);
            }
            try {
                if (this.props.loadForm) {
                    await this.props.loadForm(formData, componentConfig, this.props);
                }
            } catch (e) {
                console.error(e);
            }
            return formData;
        }

        reloadForm = async () => {
            const componentConfig = this.props.componentConfig;
            const data = {...this.props.values, ...(this.props.presets || {})};
            let formData = {};
            if (data && data[componentConfig.idfield]) {
                formData = await sendRequest({
                    option: componentName,
                    view: props.view || "default",
                    resptype: "json",
                    [componentConfig.idfield]: data[componentConfig.idfield]
                });
                await this.props.setFormValues(formData, true);
            }
            try {
                if (this.props.reloadForm) {
                    await this.props.reloadForm(formData, componentConfig, this.props);
                }
            } catch (e) {
                console.error(e);
            }
            return formData;
        }

        saveForm = async () => {
            let data = {...this.props.values, ...(this.props.presets || {})};
            const saveForm = async ({data}) => {
                const componentConfig = this.props.componentConfig;
                let params = {};
                let action = (!data[componentConfig.idfield] ? "create" : "update");
                if (props.componentActions) {
                    const actionsData = props.componentActions instanceof Function ? (await props.componentActions(action, data, params, componentConfig)) : (typeof props.componentActions === "object" ? props.componentActions[action] : props.componentActions);
                    if (typeof (actionsData) === "string") {
                        action = actionsData;
                    } else if (typeof (actionsData) === "object") {
                        action = actionsData.action || action;
                        data = actionsData.data || data;
                        params = actionsData.params || params;
                    }
                }

                /*if (Validators.editors[this.props.componentEditor]) {
                    const notValid = Validator(Validators.editors[this.props.componentEditor], data, '', this.props.componentEditor);
                    if (notValid) {
                        console.log(notValid);
                        throw notValid;
                    }
                }*/

                let requestData = {
                    action: componentName + "." + action,
                    view: props.view || "default",
                    ...params,
                    data
                };
                if (data[componentConfig.idfield]) {
                    requestData[componentConfig.idfield] = data[componentConfig.idfield];
                }
                const response = await postData(requestData, {}, {showError: false});
                if (response.success) {
                    if (response.data[componentConfig.idfield]) {
                        await this.props.setInitialValues(response.data);
                        await this.props.setFormValues(response.data);
                    }
                    try {
                        if (this.props.saveForm) {
                            await this.props.saveForm(response.data, componentConfig, {...this.props, ...this});
                        }
                    } catch (e) {
                        console.error(e);
                    }
                    return response.data;
                } else {
                    console.error(response);
                    throw response;
                }
            }
            if (props.saveForm) {
                await props.saveForm({data, saveForm});
            }
        }

        deleteForm = async (e, data = null) => {
            const componentConfig = this.props.componentConfig;
            data = data || {...this.props.values, ...(this.props.presets || {})};
            let params = {};
            let action = "delete";
            if (props.componentActions) {
                const actionsData = props.componentActions instanceof Function ? (await props.componentActions(action, data, params, componentConfig)) : (typeof props.componentActions === "object" ? props.componentActions["delete"] : props.componentActions);
                if (typeof(actionsData) === "string") {
                    action = actionsData;
                } else if (typeof(actionsData) === "object") {
                    action = actionsData.action || action;
                    data = actionsData.data || data;
                    params = actionsData.params || params;
                }
            }
            const id = data[componentConfig.idfield];
            if (id) {
                const response = await postData({
                    action: componentName + "." + action,
                    [componentConfig.idfield]: id,
                    ...params,
                }, {}, {showError: false});
                if (response.success) {
                    //await this.props.setInitialValues(this.props.pristineValues);
                    //await this.props.setFormValues(this.props.pristineValues);
                    try {
                        if(this.props.deleteForm) {
                            await this.props.deleteForm(componentConfig, this.props);
                        }
                    } catch (e) {
                        console.error(e);
                    }
                } else {
                    console.error(response);
                    throw response;
                }
            }
        }

        importFile = async (field, value) => {
            const componentConfig = await getComponentConfig(componentName);
            let data = {...this.props.values, ...(this.props.presets || {})};
            let params = {};
            let action = "import";
            if (props.componentActions) {
                const actionsData = props.componentActions instanceof Function ? (await props.componentActions(action, data, params, componentConfig)) : (typeof props.componentActions === "object" ? props.componentActions["delete"] : props.componentActions);
                if (typeof(actionsData) === "string") {
                } else if (typeof(actionsData) === "object") {
                    data = actionsData.data || data;
                    params = actionsData.params || params;
                }
            }
            return await fileUpload({action: this.props.componentConfig.componentname + ".import", file: value, data, ...params});
        }

        getFieldProps = (name) => {
            /*let props = {};
            const disabled = this.props.disabled;//Array.isArray(this.props.disabled) ? this.props.disabled : [this.props.disabled];
            props.disabled = (!Array.isArray(disabled) && (disabled || get(disabled, name, false))) || (Array.isArray(disabled) && disabled.indexOf(name) !== -1);
            if (this.props.fieldprops) {
                props = {
                    ...get(this.props.fieldprops, name, {}),
                    ...props,
                    disabled: (get(this.props.fieldprops, name, {}) || {}).disabled || props.disabled
                };
            }
            return props;*/
            let props = {
                //required: true
            };
            if (this.props.fieldProps) {
                props = this.props.fieldProps instanceof Function ? this.props.fieldProps(name) : (typeof (this.props.fieldProps) === "object" ? this.props.fieldProps[name] : (this.props.fieldProps || {}) );
            }
            props.formId = this.props.formId;
            return props;
        }

        getFieldsProps = (names) => {
            const props = {};
            names.map((name) => props[name] = this.getFieldProps(name));
            return props;
        }

        initialForm = async () => {
            await this.props.setFormValues(this.props.initialValues, true);
            try {
                if (this.props.initialForm) {
                    await this.props.initialForm(this.props.initialValues);
                }
            } catch (e) {
                console.log(e);
            }
            return this.props.initialValues;
        }

        pristineForm = async () => {
            let pristineValues = JSON.parse(JSON.stringify(this.props.pristineValues));
            try {
                if (this.props.pristineForm) {
                    pristineValues = await this.props.pristineForm(JSON.parse(JSON.stringify(pristineValues))) || pristineValues;
                }
            } catch (e) {
                console.log(e);
            }
            await this.props.setFormValues(pristineValues, true);
            return pristineValues;
        }

        render() {
            if (this.props.editorIsLoading) {
                return this.props.loader !== undefined ? this.props.loader : <ContentLoader/>;
            }
            const values = {...this.props.values, ...(this.props.presets || {})};
            const initialValues = {...this.props.initialValues, ...(this.props.presets || {})};

            const props = {...this.props, values, initialValues, ...{
                    fieldProps: this.props.fieldProps,
                    setFieldValue: this.props.setFieldValue,
                    setFormValues: this.props.setFormValues,
                    setPristineValues: this.props.setPristineValues,
                    setInitialValues: this.props.setInitialValues,
                    processAction: this.processAction,
                    importFile: this.importFile,
                    loadForm: this.loadForm,
                    reloadForm: this.reloadForm,
                    saveForm: this.saveForm,
                    initialForm: this.initialForm,
                    pristineForm: this.pristineForm,
                    getFieldProps: this.getFieldProps,
                    getFieldsProps: this.getFieldsProps,
                    deleteForm: this.deleteForm
                }};
            return (<EditorErrorBoundary><ComponentEditor {...props}><WrappedComponent /></ComponentEditor></EditorErrorBoundary>);
        }
    }

    class BackendHOCComponentEditor extends React.Component {

        static placeHolder = PlaceHolderComponent;

        state = {
            formId: new Date().getTime(),
            formValues: {},
            isSaving: false,
        };

        promisedSetState = (newState) => {
            return new Promise((resolve) => {
                this.setState(newState, () => {
                    resolve()
                });
            });
        }

        pristineForm = async (pristineValues) => {
            return await this.saveChanges(pristineValues, null);
        }

        saveChanges = async (newValues, oldValues) => {
            return await new Promise(async (resolve, reject) => {
                if (!isEqual(newValues, oldValues)) {
                    let formValues = {};
                    const componentConfig = this.props.componentConfig;
                    let data = {...newValues, ...(this.props.presets || {})};
                    let oldData = {...oldValues, ...(this.props.presets || {})};
                    let params = {};
                    let action = (!data[componentConfig.idfield] ? "create" : "update");
                    if (props.componentActions) {
                        const actionsData = props.componentActions instanceof Function ? (await props.componentActions(action, oldData, params, componentConfig)) : (typeof props.componentActions === "object" ? props.componentActions[action] : props.componentActions);
                        if (typeof(actionsData) === "object") {
                            oldData = actionsData.data || data;
                        }
                    }
                    if (props.componentActions) {
                        const actionsData = props.componentActions instanceof Function ? (await props.componentActions(action, data, params, componentConfig)) : (typeof props.componentActions === "object" ? props.componentActions[action] : props.componentActions);
                        if (typeof(actionsData) === "string") {
                            action = actionsData;
                        } else if (typeof(actionsData) === "object") {
                            action = actionsData.action || action;
                            data = actionsData.data || data;
                            params = actionsData.params || params;
                        }
                    }
                    // if (JSON.stringify(oldData) === JSON.stringify(data)) {
                    //     formValues = newValues;
                    //     const componentEditor = this.props.editorContext || editor || componentName;
                    //     store.dispatch(setFormValues(componentEditor, newValues));
                    //     this.promisedSetState({formId: new Date().getTime(), formValues});
                    //     if (this.props.setContent) {
                    //         this.props.setContent(this.props.componentEditor, formValues[this.props.componentConfig.idfield] ? {[this.props.componentConfig.idfield]: formValues[this.props.componentConfig.idfield]} : {});
                    //     }
                    //     resolve(formValues || {});
                    // } else {
                        let requestData = {
                            action: componentName + "." + action,
                            view: props.view || "default",
                            ...params,
                            data
                        };
                        if (data[componentConfig.idfield]) {
                            requestData[componentConfig.idfield] = data[componentConfig.idfield];
                        }
                        this.saveRequest && this.saveRequest.abort && this.saveRequest.abort();
                        this.saveRequest = postData(requestData);
                        this.saveRequest.then((response) => {
                            if (response.success) {
                                formValues = JSON.parse(JSON.stringify(response.data));
                            } else {
                                console.error(response);
                                reject(response);
                            }
                            const componentEditor = this.props.editorContext || editor || componentName;
                            store.dispatch(setFormValues(componentEditor, newValues));
                            this.promisedSetState({formId: new Date().getTime(), formValues});
                            if (this.props.setContent) {
                                this.props.setContent(this.props.componentEditor, formValues[this.props.componentConfig.idfield] ? {[this.props.componentConfig.idfield]: formValues[this.props.componentConfig.idfield]} : {});
                            }
                            resolve(formValues || {});
                        });
                    // }
                } else {
                    resolve(this.state.formValues || {});
                }
            })
        }

        deleteForm = async (componentConfig, props) => {
            if(this.props.deleteForm) {
                await this.props.deleteForm(componentConfig, props);
            }
            await this.promisedSetState({formId: new Date().getTime()});
        }

        render() {
            const componentEditor = this.props.editorContext || editor || componentName;

            const actions = {
                setFieldValue: (field, value) => {
                    return new Promise((resolve, reject) => {
                        this.setState({isSaving: true}, async () => {
                            const newValue = JSON.parse(JSON.stringify(this.state.formValues || {}));
                            set(newValue, field, value);
                            await this.saveChanges(newValue, JSON.parse(JSON.stringify(this.state.formValues || {})));
                            this.setState({isSaving: false});
                            resolve();
                        });
                    });
                },
                setFormValues: (values) => {
                    return new Promise((resolve, reject) => {
                        this.setState({isSaving: true}, async () => {
                            await this.saveChanges(JSON.parse(JSON.stringify(values)), JSON.parse(JSON.stringify(this.state.formValues || {})));
                            this.setState({isSaving: false});
                            resolve();
                        });
                    });
                },
                setPristineValues: () => {
                    console.log("setPristineValues Nothing to do!");
                },
                setInitialValues: () => {
                    console.log("setInitialValues Nothing to do!");
                }
            };

            let values = JSON.parse(JSON.stringify(this.state.formValues || {}));

            const dirty = !!(dirtyFlag instanceof Function ? dirtyFlag(values) : dirtyFlag);

            const subProps = {
                initialValues: values,
                pristineValues: {},
                values,
                dirty,
                componentName,
                componentEditor,
                selection: values,
                formId: this.state.formId,
                ...props,
                ...this.props,
                ...actions,
                isSaving: this.state.isSaving || this.props.isSaving,
                setEditorConfig: (config) => {
                    store.dispatch(setEditorConfig(componentEditor, config));
                },
                pristineForm: this.pristineForm,
                deleteForm: this.deleteForm,
            };
            return <BackendComponentEditor {...subProps} />
        }
    }

    const con = (props) => <ComponentConfig componentName={componentName} {...props}><BackendHOCComponentEditor/></ComponentConfig>;

    return connectAdvanced((dispatch) => {
            const bindActions = bindActionCreators({
                setFieldValue,
                setFormValues,
                setPristineValues,
                setInitialValues,
                setEditorConfig,
            }, dispatch);
            return (store, nextOwnProps) => {
                const componentEditor = nextOwnProps.editorContext || editor || componentName;
                if (store && store.editors[componentEditor] === undefined) {
                    try {
                        addEditor(componentEditor, createEditorReducer(componentEditor, props.initialState || {}));
                        dispatch({type: REDUX_EDITOR_INIT});
                    } catch (e) {

                    }
                    return {editorIsLoading: true};
                }
                const formId = store.editors[componentEditor].formId;
                const _setEditorConfig = (config) => {
                    dispatch(setEditorConfig(componentEditor, config));
                }
                return {
                    user: store.app.user,
                    menu: store.app.template.menu,
                    ...bindEditorActions(componentEditor, bindActions),
                    setEditorConfig: _setEditorConfig,
                    componentName,
                    componentEditor,
                    formId,
                    ...props,
                    ...nextOwnProps
                };
            }
        }
    )(con);
};

const createEditor = ({componentName, WrappedComponent, editor = null, props = {}, dirtyFlag = null}) => {
    return createReduxEditor({componentName, WrappedComponent, reduxEditor: editor, props, dirtyFlag});
}

export default createEditor;

export const PlaceHolderComponent = () => <Fragment>
    <Box
        header={
            <div className="ph-item">
                <div className="ph-col-12">
                    <div className="ph-row">
                        <div className="ph-col-2" />
                    </div>
                </div>
            </div>
        }
    >
        <div className="ph-item">
            <div className="ph-col-12">
                <div className="ph-picture" />
                <div className="ph-row">
                    <div className="ph-col-2" />
                    <div className="ph-col-2 empty" />
                    <div className="ph-col-4" />
                    <div className="ph-col-2 empty" />
                    <div className="ph-col-2" />
                    <div className="ph-col-6" />
                    <div className="ph-col-8" />
                </div>
            </div>
        </div>
    </Box>
    <Box
        header={
            <div className="ph-item">
                <div className="ph-col-12">
                    <div className="ph-row">
                        <div className="ph-col-2" />
                    </div>
                </div>
            </div>
        }
    >
        <div className="ph-item">
            <div className="ph-col-12">
                <div className="ph-picture" />
                <div className="ph-row">
                    <div className="ph-col-2" />
                    <div className="ph-col-2 empty" />
                    <div className="ph-col-4" />
                    <div className="ph-col-2 empty" />
                    <div className="ph-col-2" />
                    <div className="ph-col-6" />
                    <div className="ph-col-8" />
                </div>
            </div>
        </div>
    </Box>
</Fragment>;